
import React, { useState } from 'react';
import Badge from '../base/Badge';
import Button from '../base/Button';

interface Procurement {
  id: number;
  title: string;
  description: string;
  city: string;
  category: string;
  value_band: string;
  value_estimate: string;
  planned_competition_start: string;
  planned_competition_end: string;
  status: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  updated_at: string;
  tags: string[];
}

interface ProcurementCardProps {
  procurement: Procurement;
  onViewDetails: (id: number) => void;
  onFollow: (id: number) => void;
}

export default function ProcurementCard({ procurement, onViewDetails, onFollow }: ProcurementCardProps) {
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fi-FI', { day: 'numeric', month: 'numeric', year: 'numeric' });
  };

  const getValueBandVariant = (band: string) => {
    const variants: { [key: string]: 'value-i' | 'value-ii' | 'value-iii' | 'value-iv' } = {
      'I': 'value-i',
      'II': 'value-ii',
      'III': 'value-iii',
      'IV': 'value-iv'
    };
    return variants[band] || 'default';
  };

  const copyEmail = (email: string) => {
    navigator.clipboard.writeText(email);
    alert('Sähköpostiosoite kopioitu leikepöydälle');
  };

  const handleFollow = () => {
    setIsFollowing(!isFollowing);
    onFollow(procurement.id);
  };

  const truncatedDescription = procurement.description.length > 150 
    ? procurement.description.substring(0, 150) + '...'
    : procurement.description;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 pr-4">
          <button
            onClick={() => onViewDetails(procurement.id)}
            className="text-lg font-semibold text-gray-900 hover:text-blue-600 text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {procurement.title}
          </button>
        </div>
        <Badge variant={getValueBandVariant(procurement.value_band)}>
          {procurement.value_band} - {procurement.value_estimate}
        </Badge>
      </div>

      {/* Meta information */}
      <div className="flex flex-wrap items-center gap-4 mb-3 text-sm text-gray-600">
        <span className="flex items-center">
          <i className="ri-building-line mr-1 text-sm w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
          {procurement.city}
        </span>
        <span className="flex items-center">
          <i className="ri-folder-line mr-1 text-sm w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
          {procurement.category}
        </span>
        <span className="flex items-center">
          <i className="ri-refresh-line mr-1 text-sm w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
          Päivitetty {formatDate(procurement.updated_at)}
        </span>
      </div>

      {/* Competition timing */}
      <div className="mb-4">
        <div className="flex items-center text-sm text-gray-700">
          <i className="ri-calendar-line mr-2 text-sm w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
          <span className="font-medium">Kilpailutus:</span>
          <span className="ml-2">
            {formatDate(procurement.planned_competition_start)} - {formatDate(procurement.planned_competition_end)}
          </span>
          <Badge variant="status" className="ml-2">
            {procurement.status}
          </Badge>
        </div>
      </div>

      {/* Description */}
      <div className="mb-4">
        <p className="text-sm text-gray-700 leading-relaxed">
          {showFullDescription ? procurement.description : truncatedDescription}
          {procurement.description.length > 150 && (
            <button
              onClick={() => setShowFullDescription(!showFullDescription)}
              className="ml-2 text-blue-600 hover:text-blue-800 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
            >
              {showFullDescription ? 'Näytä vähemmän' : 'Näytä lisää'}
            </button>
          )}
        </p>
      </div>

      {/* Contact */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center text-sm text-gray-700">
            <i className="ri-user-line mr-2 text-sm w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
            <span className="font-medium">{procurement.contact_name}</span>
          </div>
          <div className="flex items-center gap-2">
            {showContact ? (
              <>
                <button
                  onClick={() => copyEmail(procurement.contact_email)}
                  className="p-1 text-blue-600 hover:text-blue-800 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                  aria-label="Kopioi sähköpostiosoite"
                >
                  <i className="ri-mail-line text-sm w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
                </button>
                <a
                  href={`tel:${procurement.contact_phone}`}
                  className="p-1 text-blue-600 hover:text-blue-800 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                  aria-label="Soita yhteyshenkilölle"
                >
                  <i className="ri-phone-line text-sm w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
                </a>
              </>
            ) : (
              <button
                onClick={() => setShowContact(true)}
                className="text-xs text-blue-600 hover:text-blue-800 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
              >
                Näytä yhteystiedot
              </button>
            )}
          </div>
        </div>
        {showContact && (
          <div className="mt-2 text-xs text-gray-600">
            <div className="flex items-center mb-1">
              <span className="font-medium mr-2">Sähköposti:</span>
              <span>{procurement.contact_email}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium mr-2">Puhelin:</span>
              <span>{procurement.contact_phone}</span>
            </div>
          </div>
        )}
      </div>

      {/* Tags */}
      {procurement.tags && procurement.tags.length > 0 && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-1">
            {procurement.tags.map((tag, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-50 text-blue-700"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => onViewDetails(procurement.id)}
          >
            Näytä
          </Button>
          <Button
            variant={isFollowing ? "secondary" : "outline"}
            size="sm"
            onClick={handleFollow}
          >
            <i className={`ri-${isFollowing ? 'heart-fill' : 'heart-line'} mr-1 text-sm w-4 h-4 flex items-center justify-center`} aria-hidden="true"></i>
            {isFollowing ? 'Seurataan' : 'Seuraa'}
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          <a
            href={`/rss/procurement/${procurement.id}`}
            className="p-2 text-gray-400 hover:text-gray-600 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
            aria-label="RSS-syöte tälle hankinnalle"
          >
            <i className="ri-rss-line text-sm w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
          </a>
          <a
            href={`/ics/procurement/${procurement.id}`}
            className="p-2 text-gray-400 hover:text-gray-600 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
            aria-label="Lataa kalenteritiedosto"
          >
            <i className="ri-calendar-download-line text-sm w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
          </a>
        </div>
      </div>
    </div>
  );
}
